import {
  CheckBox,
  classes,
  createColumnHelper,
  RowSelectionState,
  SortingState,
  stringify,
  Table,
} from '@portal/components'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { DocumentID, DQLResult } from '../../types'
import { useDQLEditorContext } from '../dqlEditor/context'
import NoResults from './NoResults'
import { valueRenderer } from './utils'

// TODO(aaron): After V1 DQL, mutation results will also contain any fields queried for via
// the RETURNING clause
export type DQLMutationResult = Record<string, DocumentID>
export type Column = DQLResult | DQLMutationResult
const columnHelper = createColumnHelper<Column>()

const IndexCell = ({ index }: { index?: number }) => {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <span>{index ?? ''}</span>
    </div>
  )
}

const createSelectionColumn = (canSelectRows: boolean) => {
  return columnHelper.display({
    id: canSelectRows ? 'selection' : 'rowIndex',
    header: ({ table }) => {
      if (!canSelectRows) return <IndexCell />

      return (
        <div className="flex h-full w-full items-center justify-center">
          <CheckBox
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        </div>
      )
    },
    cell: ({ row, table }) => {
      if (!canSelectRows) {
        const tableIndex =
          table
            .getSortedRowModel()
            ?.flatRows?.findIndex((flatRow) => flatRow.id === row.id) ??
          row.index

        return <IndexCell index={tableIndex + 1} />
      }

      return (
        <div className="flex h-full w-full items-center justify-center">
          <CheckBox
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      )
    },
    size: 48,
    enableResizing: false,
    meta: {
      isNonSelectable: true,
    },
  })
}

type Props = {
  data: DQLResult[] | DQLMutationResult[]
  height: number
  width: number
  canSelectRows: boolean
  onSelectedRowsChange?: (selectedRows: (string | number)[]) => void
}

export default function QueryResultTable({
  data,
  height,
  width,
  canSelectRows,
  onSelectedRowsChange,
}: Props) {
  const { logDataAccessedEvent, activeRow, setActiveRow } =
    useDQLEditorContext()

  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const reportedRef = useRef<Record<string, boolean>>({})

  const handleSelectRow = (row?: DQLResult | DQLMutationResult) => {
    if (row && logDataAccessedEvent) {
      // TODO(aaron): This is not overly correct, at least from the perspective of persisting
      // the document's ID in the audit log. It needs to be a valid query string...
      const key = typeof row._id === 'string' ? row._id : stringify(row._id)
      const isUnreported = !reportedRef.current[key]
      if (isUnreported) {
        reportedRef.current[key] = true
        logDataAccessedEvent({
          description: 'A user viewed a document',
          documentId: key,
        })
      }
    }

    // If the row is already active, unset it which will cause the data drawer to close
    if (activeRow?._id === row?._id) {
      setActiveRow(undefined)
      return
    }

    setActiveRow(row ?? undefined)
  }

  // TODO: this is not overly ideal, without some sort of schema or INFO query that provides
  // one, we have to sample and effectively guess at the keys for the entire result set. I
  // try and make the best of it, but long term this should be revisited.
  const handleSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState),
    ) => {
      const newRowSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue

      setRowSelection(newRowSelection)

      // Convert row selection state to array of document IDs. Handles
      // all types of IDs (string, number, object) possible within DQL.
      const selectedDocIds = Object.keys(newRowSelection)
        .filter((key) => newRowSelection[key])
        .map((key) => {
          const id = data[parseInt(key)]._id

          if (typeof id === 'string' || typeof id === 'number') {
            return id
          } else {
            return stringify(id)
          }
        })

      onSelectedRowsChange?.(selectedDocIds)
    },
    [data, onSelectedRowsChange, rowSelection],
  )

  const dataKeys = useMemo(() => {
    if (!data.length) return []

    const sampleSize = Math.min(data.length, 50)
    const sample = data
      .map((row) => Object.keys(row))
      .sort(() => Math.random() - 0.5)
      .slice(0, sampleSize)

    return Array.from(
      new Set(sample.reduce((acc, keys) => [...acc, ...keys], [])),
    )
  }, [data])

  const columns = useMemo(() => {
    const dynamicColumns = dataKeys.map((key) =>
      columnHelper.accessor(key, {
        header: () => <span className="px-2.5 font-plex-mono">{key}</span>,
        // react-table defaults a columns size to >= 150 and then stretches to fit the
        // remaining space. This means our row index indicator would also stretch, so to counter
        // this I am manually defining a size for each column if the total default size is less
        size:
          dataKeys.length * 150 < width ? width / dataKeys.length - 48 : 150,
        cell: ({ row: { original } }) => valueRenderer(original[key]),
        meta: { enableCopy: true }, // enableCopy is passed to TableCell so that the copy button is displayed
      }),
    )

    const selectionColumn = createSelectionColumn(canSelectRows)

    return [selectionColumn, ...dynamicColumns]
  }, [dataKeys, width, canSelectRows])

  if (!data.length)
    return (
      <div style={{ height, width }}>
        <NoResults />
      </div>
    )

  return (
    <div style={{ height, width }}>
      <Table
        data={data}
        columns={columns}
        height={height}
        width={width}
        divide="cells"
        tableCellClassName="first:px-0 px-4 font-plex-mono whitespace-nowrap overflow-hidden overflow-ellipsis"
        tableRowClassName={(isActive) =>
          classes('bg-background hover:bg-background-surface', {
            'bg-background-surface': isActive,
          })
        }
        tableHeaderCellClassName="bg-background-surface"
        activeRowOverlayTitle="Result"
        activeRow={activeRow}
        onSelectActiveRow={handleSelectRow}
        options={{
          state: {
            sorting,
            rowSelection,
          },
          onRowSelectionChange: handleSelectionChange,
          enableRowSelection: canSelectRows,
          onSortingChange: setSorting,
        }}
      />
    </div>
  )
}
